// ══════════════════════════════════════════════════════════════════
//  화면 채움 — **낮과 밤이 같은 규칙을 쓴다** 【사장님 지시 2026-08-01】
//
//  원문: *"내가 원하는건 비율이나 이런게 아니라 화면에 꽉 차는걸 원하는거야.
//         만약에 와이드스크린이나 서피스나 노트북이면 테두리를 깔끔하게 둘르라고.
//         창모드도 마찬가지."*
//
//  ■ 규칙은 하나다 — **남는 자리가 없으면 테두리도 없다.**
//      · 창이 16:9 면 그림이 화면을 100% 먹는다. 테두리 0 · 모서리 0 · 그림자 0.
//      · 16:9 가 아니면 비율을 지키고, 남는 쪽에 **한 줄**을 두른다.
//    창 모드·전체화면을 안 가린다. 창이 어떤 비율이냐만 본다.
//
//  ■ **왜 CSS 미디어쿼리로 안 하는가.** `@media (aspect-ratio: 16/9)` 는
//    「정확히」를 요구하는데, 실제 창은 브라우저 UI·DPI 배율·스크롤바 때문에
//    1919.6 × 1080.4 같은 값으로 온다. 재서 정하는 것이 맞다.
//
//  ■ ⚠ **여기가 그 규칙의 유일한 자리다.** 낮(`day.html`)과 밤(`night.html`)이
//    각자 판정하면 반드시 갈린다 — 2026-08-01 감사에서 개 자리표가 정확히 그렇게
//    갈려 있었다(`layout.js` 는 52 로 고쳤는데 실행된 것은 딴 데 박힌 26 이었다).
//    화면을 새로 만들면 **이 함수를 부른다.** 규칙을 베껴 쓰지 않는다.
// ══════════════════════════════════════════════════════════════════

/** 무대 비율 — 낮 디오라마(1920×1080)도 밤 필드(1920×1080)도 같다. */
export const ASPECT = 16 / 9;

/** 이만큼 이하로 남는 것은 **안 남은 것으로 친다.**
 *  1~2px 짜리 띠에 테두리를 두르면 선이 잘려 오히려 지저분하다. */
export const SNUG_EPS = 3;

/**
 * 이 창에서 남는 자리가 있는가.
 * @returns {'snug'|'framed'} snug = 꽉 찬다(테두리 없음) / framed = 남는다(테두리)
 */
export function fitFor(vw, vh, aspect = ASPECT) {
  if (!(vw > 0) || !(vh > 0)) return 'framed';
  // 가로로 남는 몫과 세로로 남는 몫. 창이 16:9 면 둘 다 0 이고,
  // 아니면 **둘 다** 0 보다 크다(한쪽 비율이 어긋나면 다른 쪽도 어긋난다).
  const slackX = Math.abs(vw - vh * aspect);
  const slackY = Math.abs(vh - vw / aspect);
  return Math.min(slackX, slackY) <= SNUG_EPS ? 'snug' : 'framed';
}

/** 지금 창의 크기 — 스크롤바를 뺀 실제 그릴 수 있는 자리. */
export function viewport() {
  const d = document.documentElement;
  return { vw: d.clientWidth || innerWidth, vh: d.clientHeight || innerHeight };
}

/**
 * `el.dataset.fit` 을 계속 맞춰 준다. CSS 는 `[data-fit="snug"]` 로 갈린다.
 *
 * ⚠ **되먹임이 없다.** 재는 것은 창이지 무대가 아니라서, 테두리를 켜고 끄는 것이
 *   다음 측정을 바꾸지 않는다. (무대를 재면 켜고 끄기가 무한히 반복될 수 있다.)
 *
 * @param {Element} el      `data-fit` 을 받을 요소
 * @param {object}  opts    aspect: 무대 비율 / onChange: 값이 바뀔 때 부를 것
 * @returns {() => void}    구독을 끊는 함수
 */
export function watchFit(el, { aspect = ASPECT, onChange = null } = {}) {
  if (!el) return () => {};
  let last = null;
  const apply = () => {
    const { vw, vh } = viewport();
    const mode = fitFor(vw, vh, aspect);
    if (mode === last) return;
    last = mode;
    el.dataset.fit = mode;
    onChange?.(mode, vw, vh);
  };
  apply();

  // ⚠⚠ **`resize` 이벤트만 믿으면 안 된다** 【2026-08-01 실측으로 걸렸다】
  //   창을 1920×1080 → 1920×1200 으로 바꿨는데 `resize` 가 **한 번도 안 왔고**,
  //   그래서 120px 레터박스가 생겼는데도 테두리가 안 나왔다(`fitFor` 는 그 순간에도
  //   `framed` 를 정확히 돌려주고 있었다 — 판정이 아니라 **깨우는 쪽**이 문제였다).
  //   `ResizeObserver` 는 크기가 바뀌면 반드시 발화한다. **이쪽을 정본으로 두고**
  //   이벤트들은 보조로 남긴다(전체화면 전환처럼 크기가 같아도 다시 재야 할 때가 있다).
  const ro = new ResizeObserver(apply);
  ro.observe(document.documentElement);

  // 마지막 보험 — **느린 되풀이 검사.** 하는 일은 정수 둘 비교라 사실상 공짜고,
  //   위의 셋(RO·resize·fullscreenchange)이 전부 안 오는 환경이 실제로 있다.
  //   스팀 래퍼처럼 창을 바깥에서 주무르는 환경에서 테두리가 틀린 채로 굳는 것보다
  //   0.4초 늦게라도 맞는 편이 낫다. 잡히면 그 즉시 `apply` 가 알아서 걸러 낸다.
  const tick = setInterval(apply, 400);

  addEventListener('resize', apply);
  addEventListener('orientationchange', apply);
  // F11 은 resize 만 내지만, Fullscreen API(스팀 래퍼·`requestFullscreen`)는
  // 전환이 끝나는 시점이 resize 보다 늦을 수 있다 — 둘 다 듣는다.
  document.addEventListener('fullscreenchange', apply);
  // 주소창이 접히는 브라우저는 창 크기가 resize 없이 바뀐다.
  visualViewport?.addEventListener('resize', apply);
  return () => {
    ro.disconnect();
    clearInterval(tick);
    removeEventListener('resize', apply);
    removeEventListener('orientationchange', apply);
    document.removeEventListener('fullscreenchange', apply);
    visualViewport?.removeEventListener('resize', apply);
  };
}
